import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { AttendanceStatusBadge, pctColor, DEFAULT_ATTENDANCE_THRESHOLD } from "@/lib/attendance";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, AlertTriangle, BarChart3, CalendarX2, CalendarClock, Save } from "lucide-react";

export default function AttendanceReport() {
    const { user } = useAuth();
    const isAdmin = user?.role === "ADMIN";
    const [batches, setBatches] = useState([]);
    const [batchId, setBatchId] = useState("");
    const [report, setReport] = useState(null);
    const [drill, setDrill] = useState(null);
    const [threshold, setThreshold] = useState("");
    const [savingThreshold, setSavingThreshold] = useState(false);

    useEffect(() => {
        // Already role-scoped by the backend: FACULTY only see their own batches.
        api.get("/batches").then(r => {
            setBatches(r.data.items);
            if (r.data.items.length) setBatchId(prev => prev || r.data.items[0].id);
        }).catch(() => {});
    }, []);

    const load = async () => {
        if (!batchId) return;
        try {
            const { data } = await api.get(`/attendance/report/batch/${batchId}`);
            setReport(data);
            setThreshold(String(data.threshold));
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not load the report");
            setReport(null);
        }
    };
    useEffect(() => { load(); }, [batchId]); // eslint-disable-line

    const openStudent = async (studentId) => {
        try {
            const { data } = await api.get(`/attendance/report/student/${studentId}`);
            setDrill(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not load that student");
        }
    };

    const exportCsv = async () => {
        try {
            const resp = await api.get(`/attendance/report/batch/${batchId}/export`, { responseType: "blob" });
            const url = URL.createObjectURL(resp.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `attendance-${(report?.batch?.name || batchId).replace(/\s+/g, "-")}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error("Export failed");
        }
    };

    const saveThreshold = async () => {
        const v = parseFloat(threshold);
        if (Number.isNaN(v) || v < 0 || v > 100) { toast.error("Threshold must be between 0 and 100"); return; }
        setSavingThreshold(true);
        try {
            await api.put("/settings", { attendance_threshold: v });
            toast.success(`Low-attendance threshold set to ${v}%`);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not save the threshold");
        } finally { setSavingThreshold(false); }
    };

    const live = report?.threshold ?? DEFAULT_ATTENDANCE_THRESHOLD;
    const flagged = report?.students?.filter(s => s.below_threshold) || [];

    return (
        <div className="space-y-6" data-testid="attendance-report-page">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Reports</div>
                    <h1 className="font-display text-3xl lg:text-4xl mt-1">Attendance</h1>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                    <Select value={batchId} onValueChange={setBatchId}>
                        <SelectTrigger className="w-56" data-testid="report-batch-select"><SelectValue placeholder="Select batch" /></SelectTrigger>
                        <SelectContent>{batches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button className="mx-btn-primary" onClick={exportCsv} disabled={!report} data-testid="export-csv-btn">
                        <Download className="h-4 w-4 mr-2" /> Export CSV
                    </Button>
                </div>
            </div>

            {!report ? (
                <div className="mx-card p-12 text-center text-muted-foreground">
                    {batches.length === 0 ? "No batches available." : "Select a batch to see its attendance report."}
                </div>
            ) : (
                <>
                    {/* Stat tiles */}
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        <Card className="mx-card">
                            <CardContent className="p-5">
                                <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest font-bold">
                                    <BarChart3 className="h-3.5 w-3.5" /> Sessions counted
                                </div>
                                <div className="font-display text-3xl mt-2" data-testid="completed-count">{report.session_counts.completed}</div>
                                <div className="text-xs text-muted-foreground mt-1">
                                    completed of {report.session_counts.total}
                                    {report.session_counts.open > 0 && ` · ${report.session_counts.open} still open`}
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="mx-card">
                            <CardContent className="p-5">
                                <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest font-bold">
                                    <CalendarX2 className="h-3.5 w-3.5" /> Cancelled
                                </div>
                                <div className="font-display text-3xl mt-2" data-testid="cancelled-count">{report.session_counts.cancelled}</div>
                                <div className="text-xs text-muted-foreground mt-1">excluded from all maths</div>
                            </CardContent>
                        </Card>
                        <Card className="mx-card">
                            <CardContent className="p-5">
                                <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest font-bold">
                                    <CalendarClock className="h-3.5 w-3.5" /> Backdated
                                </div>
                                <div className="font-display text-3xl mt-2" data-testid="backdated-count">{report.session_counts.backdated}</div>
                                <div className="text-xs text-muted-foreground mt-1">entered after the fact</div>
                            </CardContent>
                        </Card>
                        <Card className="mx-card">
                            <CardContent className="p-5">
                                <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest font-bold">
                                    <AlertTriangle className="h-3.5 w-3.5" /> Below {live}%
                                </div>
                                <div className={`font-display text-3xl mt-2 ${flagged.length ? "text-[#E11D48]" : ""}`} data-testid="flagged-count">
                                    {flagged.length}
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">students flagged</div>
                            </CardContent>
                        </Card>
                    </div>

                    {isAdmin && (
                        <Card className="mx-card">
                            <CardContent className="p-4 flex flex-wrap items-end gap-3">
                                <div>
                                    <Label>Low-attendance threshold (%)</Label>
                                    <Input type="number" min="0" max="100" className="w-32" value={threshold}
                                        onChange={e => setThreshold(e.target.value)} data-testid="threshold-input" />
                                </div>
                                <Button variant="outline" className="rounded-xl" onClick={saveThreshold} disabled={savingThreshold} data-testid="save-threshold-btn">
                                    <Save className="h-4 w-4 mr-2" /> Save threshold
                                </Button>
                                <div className="text-xs text-muted-foreground ml-auto max-w-md">
                                    Applies everywhere attendance is flagged. Students are only counted from the date they joined the batch, and cancelled sessions never count.
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    <Card className="mx-card">
                        <CardContent className="p-0 overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Student</TableHead>
                                        <TableHead>Joined batch</TableHead>
                                        <TableHead>Counted</TableHead>
                                        <TableHead>Present</TableHead>
                                        <TableHead>Late</TableHead>
                                        <TableHead>Absent</TableHead>
                                        <TableHead>Attendance</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {/* Pre-sorted worst-first by the backend. */}
                                    {report.students.map(s => (
                                        <TableRow key={s.student_id} className="cursor-pointer" onClick={() => openStudent(s.student_id)}
                                            data-testid={`report-row-${s.student_id}`}>
                                            <TableCell>
                                                <div className="flex items-center gap-3">
                                                    <div className="h-9 w-9 rounded-full bg-brand-gradient flex items-center justify-center text-white font-bold text-sm">
                                                        {s.name?.[0] || "?"}
                                                    </div>
                                                    <div>
                                                        <div className="font-semibold text-sm flex items-center gap-2">
                                                            {s.name}
                                                            {s.below_threshold && (
                                                                <Badge className="bg-[#E11D48] hover:bg-[#E11D48] text-[10px]" data-testid={`low-flag-${s.student_id}`}>
                                                                    Low
                                                                </Badge>
                                                            )}
                                                        </div>
                                                        <div className="text-xs text-muted-foreground font-mono">{s.enrollment_number || "—"}</div>
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-sm text-muted-foreground">{s.batch_joined_at || "—"}</TableCell>
                                            <TableCell className="text-sm">{s.total}</TableCell>
                                            <TableCell className="text-sm">{s.present}</TableCell>
                                            <TableCell className="text-sm">{s.late}</TableCell>
                                            <TableCell className="text-sm">{s.absent}</TableCell>
                                            <TableCell>
                                                <span className={`font-display text-lg ${pctColor(s.percentage, live)}`}>
                                                    {s.total === 0 ? "—" : `${s.percentage}%`}
                                                </span>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {report.students.length === 0 && (
                                        <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-12">No active students in this batch.</TableCell></TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </>
            )}

            {/* Per-student drill-down */}
            <Dialog open={!!drill} onOpenChange={v => { if (!v) setDrill(null); }}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>{drill?.student?.name}</DialogTitle>
                        <DialogDescription>
                            {drill?.student?.enrollment_number} · joined batch {drill?.join_date || "—"}
                            {drill?.below_threshold && " · flagged as low attendance"}
                        </DialogDescription>
                    </DialogHeader>
                    {drill && (
                        <div className="space-y-4">
                            <div className="flex flex-wrap gap-2">
                                <Badge variant="secondary">{drill.overall?.total || 0} counted</Badge>
                                <Badge className="bg-[#16A34A] hover:bg-[#16A34A]">{drill.overall?.present || 0} present</Badge>
                                <Badge className="bg-[#FFA81B] hover:bg-[#FFA81B]">{drill.overall?.late || 0} late</Badge>
                                <Badge className="bg-[#E11D48] hover:bg-[#E11D48]">{drill.overall?.absent || 0} absent</Badge>
                                <Badge variant="outline" className={pctColor(drill.overall?.percentage || 0, drill.threshold ?? live)}>
                                    {drill.overall?.percentage || 0}%
                                </Badge>
                            </div>
                            <div className="max-h-80 overflow-auto scrollbar-thin border border-border rounded-xl">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Date</TableHead>
                                            <TableHead>Course</TableHead>
                                            <TableHead>Status</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {drill.sessions.map(s => (
                                            <TableRow key={s.session_id}>
                                                <TableCell className="text-sm">{s.session_date}<div className="text-xs text-muted-foreground">{s.start_time}</div></TableCell>
                                                <TableCell className="text-sm">{s.course_title || "—"}</TableCell>
                                                <TableCell>
                                                    <AttendanceStatusBadge status={s.status} />
                                                    {!s.marked && <span className="ml-2 text-[10px] text-muted-foreground">not marked</span>}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {drill.sessions.length === 0 && (
                                            <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                                                No counted sessions since this student joined.
                                            </TableCell></TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
